import React, { useState, useEffect, useRef } from 'react';

import { Autocomplete, AutocompleteProps, AutocompleteItem } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteOtherWorkTypes from 'hooks/otherWorkType/useAutoCompleteOtherWorkTypes';

import SelectorButton from './SelectorButton';

export interface OtherWorkTypeAutoCompleteValue extends AutocompleteItem {
  otherWorkTypeId: string;
  label: string;
}

interface IOtherWorkTypeAutoCompleteProps
  extends Omit<
    AutocompleteProps,
    'data' | 'value' | 'onChange' | 'filter' | 'onItemSubmit' | 'rightSection'
  > {
  value: OtherWorkTypeAutoCompleteValue;
  onChange: (item: OtherWorkTypeAutoCompleteValue | null) => void;
}

const OtherWorkTypeAutoComplete = ({
  value,
  onChange,
  ...autocompleteProps
}: IOtherWorkTypeAutoCompleteProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<AutocompleteItem[]>([]);
  const [search, setSearch] = useState(value.label);
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data, isSuccess, isLoading } = useAutoCompleteOtherWorkTypes({
    search: debouncedSearch,
  });

  const handleSearchChange = (query: string): void => {
    setSearch(query);
    onChange(null);
  };
  const handleItemSubmit = (item: OtherWorkTypeAutoCompleteValue): void => {
    setSearch('');
    onChange(item);
  };
  const handleClearSearch = (): void => {
    setSearch('');
  };

  const RightSection = (
    <SelectorButton
      isLoading={isLoading}
      hasText={!!(value.label || search)}
      handleClick={() => handleSearchChange('')}
      handleFocus={() => inputRef?.current?.focus()}
    />
  );

  useEffect(() => {
    if (isSuccess && data) {
      setOptions(
        data.data.map(otherWorkType => ({
          ...otherWorkType,
          value: otherWorkType.otherWorkTypeId,
          label: otherWorkType.name,
        })),
      );
    }
  }, [isSuccess, data]);

  return (
    <Autocomplete
      ref={inputRef}
      rightSection={RightSection}
      value={value.label || search}
      onChange={handleSearchChange}
      onItemSubmit={handleItemSubmit}
      onDropdownClose={handleClearSearch}
      filter={() => true}
      data={options}
      placeholder='Select other work type'
      {...autocompleteProps}
    />
  );
};

export default OtherWorkTypeAutoComplete;
